import { useEffect, useState } from "react";
import { api, eur } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, MapPin, Home, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const IMGS = [
  "https://images.pexels.com/photos/6636314/pexels-photo-6636314.jpeg",
  "https://images.unsplash.com/photo-1585128792103-0b591f96512e",
  "https://images.unsplash.com/photo-1661796428215-04fc2830aae6",
  "https://images.pexels.com/photos/7005286/pexels-photo-7005286.jpeg",
];
const empty = { title: "", address: "", city: "", postal_code: "", type: "Appartement", surface: "", rooms: 1, rent_amount: "", charges_amount: "", purchase_value: "", purchase_date: "", description: "", image_url: IMGS[0], status: "disponible" };

export default function Properties() {
  const { user } = useAuth();
  const isOwner = user.role === "proprietaire" || user.role === "admin";
  const isCandidate = user.role === "candidat";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [applyFor, setApplyFor] = useState(null);
  const [applyMsg, setApplyMsg] = useState({ message: "", income: "", profession: "" });

  const load = () => api.get("/properties").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm({ ...empty, image_url: IMGS[items.length % IMGS.length] }); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, surface: Number(form.surface) || 0, rooms: Number(form.rooms) || 1, rent_amount: Number(form.rent_amount) || 0, charges_amount: Number(form.charges_amount) || 0, purchase_value: Number(form.purchase_value) || 0 };
    try {
      if (editing) await api.put(`/properties/${editing}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing ? "Bien mis à jour" : "Bien ajouté");
      setOpen(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => { await api.delete(`/properties/${id}`); toast.success("Bien supprimé"); load(); };

  const submitApplication = async (e) => {
    e.preventDefault();
    try {
      await api.post("/applications", { property_id: applyFor.id, message: applyMsg.message, income: Number(applyMsg.income) || 0, profession: applyMsg.profession });
      toast.success("Candidature envoyée");
      setApplyFor(null); setApplyMsg({ message: "", income: "", profession: "" });
    } catch (err) { toast.error("Candidature impossible"); }
  };

  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader
        title={isCandidate ? "Annonces disponibles" : "Mes biens"}
        subtitle={isCandidate ? "Postulez aux logements meublés qui vous intéressent." : "Gérez vos logements meublés LMNP / LMP."}
        action={isOwner && <Button onClick={openNew} className="rounded-full gap-2" data-testid="add-property-btn"><Plus className="w-4 h-4" /> Ajouter un bien</Button>}
      />

      {items.length === 0 ? (
        <EmptyState title="Aucun bien" desc={isOwner ? "Ajoutez votre premier logement meublé." : "Aucune annonce disponible pour le moment."} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p) => (
            <div key={p.id} className="rounded-xl border border-border bg-card overflow-hidden transition-transform hover:-translate-y-1" data-testid={`property-card-${p.id}`}>
              <div className="h-44 bg-secondary relative">
                <img src={p.image_url || IMGS[0]} alt={p.title} className="w-full h-full object-cover" />
                <Badge className="absolute top-3 left-3" variant={p.status === "disponible" ? "default" : "secondary"}>{p.status}</Badge>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-semibold text-lg">{p.title}</h3>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {p.city} {p.postal_code}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3">
                  <span className="flex items-center gap-1"><Home className="w-3.5 h-3.5" /> {p.type}</span>
                  <span>{p.surface} m²</span>
                  <span>{p.rooms} pièce(s)</span>
                </div>
                <p className="font-heading text-xl font-bold mt-3">{eur(p.rent_amount)}<span className="text-sm font-normal text-muted-foreground"> /mois</span></p>
                <div className="flex gap-2 mt-4">
                  {isCandidate && p.status === "disponible" && (
                    <Button size="sm" className="rounded-full flex-1" onClick={() => setApplyFor(p)} data-testid={`apply-btn-${p.id}`}>Postuler</Button>
                  )}
                  {isOwner && (
                    <>
                      <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p.id)} data-testid={`delete-property-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier le bien" : "Nouveau bien"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Titre</Label><Input value={form.title} onChange={set("title")} required data-testid="prop-title" /></div>
            <div><Label>Adresse</Label><Input value={form.address} onChange={set("address")} data-testid="prop-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ville</Label><Input value={form.city} onChange={set("city")} required data-testid="prop-city" /></div>
              <div><Label>Code postal</Label><Input value={form.postal_code} onChange={set("postal_code")} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Type</Label><Input value={form.type} onChange={set("type")} /></div>
              <div><Label>Surface m²</Label><Input type="number" value={form.surface} onChange={set("surface")} /></div>
              <div><Label>Pièces</Label><Input type="number" value={form.rooms} onChange={set("rooms")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Loyer /mois</Label><Input type="number" value={form.rent_amount} onChange={set("rent_amount")} data-testid="prop-rent" /></div>
              <div><Label>Charges /mois</Label><Input type="number" value={form.charges_amount} onChange={set("charges_amount")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valeur d'acquisition</Label><Input type="number" value={form.purchase_value} onChange={set("purchase_value")} data-testid="prop-value" /></div>
              <div><Label>Date d'acquisition</Label><Input type="date" value={form.purchase_date} onChange={set("purchase_date")} /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={set("description")} rows={2} /></div>
            <DialogFooter><Button type="submit" className="rounded-full" data-testid="save-property-btn">Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* apply dialog */}
      <Dialog open={!!applyFor} onOpenChange={(o) => !o && setApplyFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Postuler — {applyFor?.title}</DialogTitle></DialogHeader>
          <form onSubmit={submitApplication} className="space-y-3">
            <div><Label>Profession</Label><Input value={applyMsg.profession} onChange={(e) => setApplyMsg({ ...applyMsg, profession: e.target.value })} data-testid="apply-profession" /></div>
            <div><Label>Revenu mensuel net</Label><Input type="number" value={applyMsg.income} onChange={(e) => setApplyMsg({ ...applyMsg, income: e.target.value })} data-testid="apply-income" /></div>
            <div><Label>Message</Label><Textarea value={applyMsg.message} onChange={(e) => setApplyMsg({ ...applyMsg, message: e.target.value })} rows={3} data-testid="apply-message" /></div>
            <DialogFooter><Button type="submit" className="rounded-full" data-testid="submit-application-btn">Envoyer ma candidature</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
